import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { GET_PRESENTATIONS, GET_TEMPLATE_FEATURES } from "../graphql";

const log: any = LoggerModule?.getInstance();
export interface GetTemplateCostumeTypesProps {
  presentationId?: string;
  id?: string;
  presentationFilter?: any;
  selectedPresentation?: any;
  presentation?: any;
  location?: any;
  [key: string]: any;
}

export interface IGetTemplateCostumeTypesMethods extends IPluginMethodMap {
  getTemplateCostumeTypes(args: GetTemplateCostumeTypesProps): Promise<any>;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const getIdValue = (value: any): string | undefined => {
  if (!value) return undefined;
  if (typeof value === "string") return value;

  return value?.id ?? value?._id ?? value?.value ?? value?.presentationId;
};

const isActiveStatus = (status: any) => {
  return (
    String(status ?? "")
      .trim()
      .toLowerCase() === "active"
  );
};

const getRequestedPresentationId = (
  args: GetTemplateCostumeTypesProps,
): string | undefined => {
  return (
    args?.presentationId ??
    args?.selectedPresentation?.id ??
    args?.selectedPresentation?._id ??
    args?.selectedPresentation?.value ??
    args?.presentation?.id ??
    args?.presentation?._id ??
    args?.presentation ??
    args?.id ??
    args?.location?.state?.presentationId ??
    args?.location?.state?.id?.presentationId
  );
};

const resolveSelectedPresentation = (
  presentations: any[],
  args: GetTemplateCostumeTypesProps,
) => {
  const requestedPresentationId = getRequestedPresentationId(args);

  if (requestedPresentationId) {
    const selectedByUser = presentations.find(
      (presentation: any) =>
        getIdValue(presentation) === requestedPresentationId,
    );
    return selectedByUser ?? { id: requestedPresentationId };
  }

  const defaultPresentation = presentations.find((presentation: any) =>
    isActiveStatus(presentation?.status),
  );

  return defaultPresentation ?? presentations?.[0];
};

const mapFeature = (
  feature: any,
  presentationId: string,
  type: "global" | "section",
) => ({
  ...feature,
  id: feature.id,
  name: feature.name ?? "Untitled Feature",
  presentation: presentationId,
  assetType: type,
  isCommon: feature.isCommon === true,
  section: Array.isArray(feature.section) ? feature.section : [],
  costumeAttributes: (feature.costumeAttributes || []).map(
    (attribute: any) => ({
      ...attribute,
      id: attribute.id,
      name: attribute.name ?? "Untitled Attribute",
      presentation: presentationId,
      costumeFeature: feature.id,
      costumeValues: (attribute.costumeValues || []).map((value: any) => ({
        ...value,
        id: value.id,
        name: value.name ?? "Untitled Value",
        presentation: presentationId,
        costumeFeature: feature.id,
        costumeAttribute: attribute.id,
      })),
    }),
  ),
});

export class GetTemplateCostumeTypesService implements IAgentPlugin {
  readonly methods: IGetTemplateCostumeTypesMethods;

  constructor() {
    this.methods = {
      getTemplateCostumeTypes: this.getTemplateCostumeTypes.bind(this),
    };
  }

  async getTemplateCostumeTypes(args: GetTemplateCostumeTypesProps) {
    log.blue("GetTemplateCostumeTypesService.getTemplateCostumeTypes", args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    if (!client) {
      throw new Error("GraphQL client not found");
    }

    const presentationsRes = await client.query({
      query: GET_PRESENTATIONS,
      variables: {
        filter: args?.presentationFilter ?? undefined,
      },
      fetchPolicy: "network-only",
    });

    const presentations =
      presentationsRes?.data?.getPresentations?.content ?? [];

    const selectedPresentation = resolveSelectedPresentation(
      presentations,
      args,
    );

    const presentationId = getIdValue(selectedPresentation);

    if (!presentationId) {
      return {
        isEmpty: true,
        sectionOptions: [],
        features: [],
        globalAssets: [],
        sectionAssets: [],
        sections: [],
        presentations,
        activePresentation: undefined,
        selectedPresentation: undefined,
        defaultPresentation: undefined,
        presentationId: undefined,
      };
    }

    const featureRes = await client.query({
      query: GET_TEMPLATE_FEATURES,
      variables: {
        presentationId,
        options: {
          sort: {
            field: "position",
            order: "asc",
          },
          limit: 2000,
          skip: 0,
        },
      },
      fetchPolicy: "network-only",
    });

    const rawFeatures = featureRes?.data?.getTemplateFeatures ?? {};

    const globalAssets = (rawFeatures?.globalAssets || []).map((feature: any) =>
      mapFeature(feature, presentationId, "global"),
    );

    const sectionAssets = (rawFeatures?.sectionAssets || []).map(
      (feature: any) => mapFeature(feature, presentationId, "section"),
    );

    const features = [...globalAssets, ...sectionAssets];

    return {
      isEmpty: features.length === 0,
      sectionOptions: features,
      features,
      globalAssets,
      sectionAssets,
      sections: [],
      presentations,
      activePresentation: selectedPresentation,
      selectedPresentation,
      defaultPresentation: selectedPresentation,
      presentationId,
    };
  }
}
